import { FileProps } from '@/context/FileContext/types';

import {
  ContractData,
  ContractDataPatch,
  ContractDataPatchResult,
  DefaultPaymentFlow,
  EmissaoContratoResultado,
  IAllotmentIndication,
  IBuilderCity,
  IBuilderIndication,
  IBuilderState,
  IColdInvestmentIndication,
  IColdInvestmentIndicationListItem,
  IInvestmentIndication,
  IMarketplaceIndication,
  IPropertyIndication,
  SaleConditions,
} from '@/pages/Partners/types';

import request from '../request';

import {
  CommissionPaymentMethod,
  CreateOrganizationPartnerPayload,
  FilePayload,
  IAllotmentCity,
  IAllotmentIndicationNew,
  IAllotmentIndicationPostNew,
  IAllotmentIndicationSummary,
  IAllotmentNeighborhood,
  IBanner,
  IBuildingIndicationSummary,
  IDecorIndicationPost,
  IFranchiseIndicationPost,
  IIndicationCoordinate,
  IIndicationDetail,
  IIndicationSummary,
  IInvestmentIndicationSummary,
  ILocationIndicationDetail,
  IMarketplaceIndicationPost,
  IndicationParams,
  IPartnerIndicationPost,
  IPartnerIndicationSummary,
  IPartnerInvite,
  IPartnerWhatsAppGroup,
  IPropertyIndicationSummary,
  LocationCoverageCheckPayload,
  LocationCoverageCheckResponse,
  LocationGenerateContractPayload,
  LocationGenerateContractResponse,
  LocationIndicationCreatePayload,
  LocationIndicationDraftPayload,
  LocationIndicationListResponse,
  LocationIndicationParams,
  LocationIndicationSummary,
  LocationQualificationCheckPayload,
  LocationQualificationCheckResponse,
  Partner,
  PartnerInviteChannel,
  PartnerSearchResult,
  RankingParams,
  RankingResponse,
  RequestB2BCondominium,
  RequestOrganizationPartners,
  RequestPartner,
  RequestUser,
  UserPatch,
} from './types';

export const getPartners = async (): Promise<RequestPartner[]> => {
  const { data } = await request.get('/account/partner/');
  return data;
};

// Autocomplete de parceiro ativo por nome. `signal` permite abortar a busca
// anterior quando uma tecla nova chega antes da resposta — o endpoint tem rate
// limit de 2s por usuário, pensado pra digitação com debounce.
export const searchPartners = async (
  q: string,
  signal?: AbortSignal,
): Promise<PartnerSearchResult[]> => {
  const { data } = await request.get('/api/v1/partners/search/', { params: { q }, signal });
  return data;
};

export const getOrganizationPartners = async (): Promise<RequestOrganizationPartners[]> => {
  const { data } = await request.get('/organization/partners/me/');
  return data;
};

export const postOrganizationPartner = async (
  payload: CreateOrganizationPartnerPayload,
): Promise<RequestOrganizationPartners> => {
  const { data } = await request.post('/api/v1/partners/', payload);
  return data;
};

export const postInactivatePartner = async (partnerId: number): Promise<void> => {
  await request.post(`/api/v1/partners/${partnerId}/inactivate/`);
};

export const getUserByEmail = async (email: string): Promise<RequestUser[]> => {
  const { data } = await request.get(`/account/user/?email=${email}`);
  return data;
};

export const getUserByPipedrivePersonId = async (personId: string): Promise<any> => {
  const { data } = await request.get(`/api/v1/users/pipedrive-info/`, {
    params: { pipedrive_person_id: personId },
  });
  return data;
};

export const getPartnerById = async (id: number): Promise<RequestPartner> => {
  const { data } = await request.get(`/account/partner/${id}/`);
  return data;
};

export const postPartner = async (params: Partner) => {
  const { data } = await request.post('/account/partner/', params);
  return data;
};

export const patchPartner = async (id: number, params: Partial<Partner>) => {
  const { data } = await request.patch(`/api/v1/partners/${id}/`, params);
  return data;
};

export const deletePartner = async (id: number) => {
  await request.delete(`/account/partner/${id}/`);
};

type IndicationResponse<T> = {
  results: T[];
  total_pages: number;
  count: number;
};

export const getPropertyIndication = async (
  payload: IndicationParams,
): Promise<IndicationResponse<IPropertyIndication>> => {
  const { data } = await request.get(`/partners/v2/indications/property/`, {
    params: payload,
  });

  return { results: data.results, total_pages: data.total_pages, count: data.count };
};

export const getPropertyIndicationByID = async (id: number): Promise<IPropertyIndication[]> => {
  const { data } = await request.get(`/partners/v2/indications/property/${id}`);
  return data.results;
};

export type IPropertyIndicatePost = Omit<
  IPropertyIndication,
  'id' | 'created_at' | 'updated_at' | 'status_change_date'
>;

export const postPropertyIndication = async (
  data: IPropertyIndicatePost,
): Promise<IPropertyIndication> => {
  const partnerId = (data as any).partner ?? (data as any).partner_id ?? undefined;

  const payload = {
    ...data,
    condominium_name: `${data.condominium_name}` === '-1' ? undefined : data.condominium_name,
    partner_id: partnerId,
  };

  const { data: response } = await request.post('/partners/v2/indications/property/', payload);
  return response;
};

export const postPropertyIndicationFiles = async (indicationId: number, files: FileProps[]) => {
  if (files.length === 0) return [];

  const filesUids = files.map((item) => item.uid);
  const { data: response } = await request.post('/partners/indications/property_files/', {
    indication_id: indicationId,
    files: filesUids,
  });

  return response;
};

export const getAllotmentIndication = async (
  payload: IndicationParams,
): Promise<IndicationResponse<IAllotmentIndication>> => {
  const { data } = await request.get(`/partners/v2/indications/allotment/`, {
    params: payload,
  });

  return { results: data.results, total_pages: data.total_pages, count: data.count };
};

export const getAllotmentIndicationNew = async (
  payload: IndicationParams,
): Promise<IndicationResponse<IAllotmentIndicationNew>> => {
  const params = {
    page: payload.page,
    page_size: payload.page_size,
    status: payload.status || payload.indication_status,
    pipefy_card_external_id: payload.pipedrive_deal_id,
    year: payload.year,
    month: payload.month,
    partner_id: payload.partner_id,
  };

  const { data } = await request.get(`/partners/v2/indication/allotments/`, {
    params,
  });

  return { results: data.results, total_pages: data.total_pages, count: data.count };
};

export const getAllotmentIndicationByID = async (
  id: number,
): Promise<IAllotmentIndication[] | any> => {
  const { data } = await request.get(`/partners/v2/indication/allotments/${id}`);
  return data.results || data;
};

export type IAllotmentIndicatePost = {
  partner_id: number;
  owner_name: string;
  owner_phone_number: string;
  status: string;
  notes: string;
  zip_code: string;
  state: string;
  city: string;
  neighborhood: string;
  street: string;
  number: string;
  width: number;
  length: number;
  latitude: number;
  longitude: number;
  total_area: number;
  appraisal_value: string;
  registry_number?: string;
  files?: FilePayload[];
};

export const postAllotmentIndication = async (
  data: IAllotmentIndicatePost,
): Promise<IAllotmentIndication> => {
  const { data: response } = await request.post('/partners/v2/indication/allotments/', data);
  return response;
};

export const getInvestmentIndication = async (
  payload: IndicationParams,
): Promise<IndicationResponse<IInvestmentIndication>> => {
  const { data } = await request.get(`/partners/v2/indications/investment/`, {
    params: payload,
  });

  return { results: data.results, total_pages: data.total_pages, count: data.count };
};

export const postAllotmentIndicationFiles = async (indicationId: number, files: FilePayload[]) => {
  if (files.length === 0) return [];

  const { data: response } = await request.post('/partners/indication/allotments-files/', {
    indication_id: indicationId,
    files,
  });

  return response;
};

export const postInvestmentIndication = async (
  data: IInvestmentIndication,
): Promise<IInvestmentIndication> => {
  const partnerId = (data as any).partner ?? (data as any).partner_id ?? undefined;
  const payload = {
    ...data,
    partner_id: partnerId,
  };
  const { data: response } = await request.post('/partners/v2/indications/investment/', payload);
  return response;
};

/**
 * Fluxo padrão da unidade, para o corretor editar em vez de digitar do zero.
 *
 * É `detail=False` no backend porque a indicação ainda não existe — isto
 * alimenta o formulário de criação. Não toca o Fluxo de Obras (só lê dados do
 * SZI), por isso responde em ~2s e pode ser chamado a cada troca de unidade.
 */
export const getDefaultPaymentFlow = async (params: {
  enterprise: number;
  unit: number;
  start_date: string;
  payment_day?: number;
}): Promise<DefaultPaymentFlow> => {
  const { data } = await request.get('/partners/v2/indications/investment/default-payment-flow/', {
    params,
  });
  return data;
};

/**
 * Condições de venda da unidade + data de entrega da obra.
 *
 * Alimenta o painel de resumo: seletor de data de referência, seletor de
 * condição e os totais por tipo. Não depende do calendário escolhido, só da
 * unidade — por isso não recebe data inicial nem dia do pagamento.
 */
export const getSaleConditions = async (params: {
  enterprise: number;
  unit: number;
}): Promise<SaleConditions> => {
  const { data } = await request.get('/partners/v2/indications/investment/sale-conditions/', {
    params,
  });
  return data;
};

// Jornada unificada do parceiro — SPOT (ff_enable_partner_payment_flow)
export const submitPaymentFlow = async (
  indicationId: string,
  paymentFlow: unknown[],
  diaPagamento?: number,
): Promise<IInvestmentIndication> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/payment-flow/`,
    // `dia_pagamento` vai junto porque o backend o persiste com o fluxo: é
    // parâmetro de request, não propriedade das linhas, e a reserva precisa
    // reenviar ao motor o MESMO calendário que foi aprovado.
    diaPagamento
      ? { payment_flow: paymentFlow, dia_pagamento: diaPagamento }
      : { payment_flow: paymentFlow },
  );
  return data;
};

export const getIndicationJourney = async (
  indicationId: string,
): Promise<IInvestmentIndication> => {
  const { data } = await request.get(
    `/partners/v2/indications/investment/${indicationId}/journey/`,
  );
  return data;
};

/**
 * Efetiva a reserva da unidade para um fluxo aprovado.
 *
 * Sem corpo de propósito: fluxo, `base_version` e `numero_pedido` saem do
 * veredito gravado no banco, nunca do request — mandá-los daqui deixaria o
 * parceiro reservar contra um veredito que ele mesmo escreveu.
 *
 * Efeito real e difícil de desfazer: cria reserva no Fluxo de Obras.
 */
export const reserveUnit = async (
  indicationId: string,
): Promise<{ journey_step: string; reservado: boolean; card_id: string | null }> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/reserve/`,
  );
  return data;
};

/**
 * Troca a unidade da indicação, mantendo a MESMA indicação.
 *
 * `unit` e nada mais no corpo: pela regra 4 a troca é dentro do mesmo
 * empreendimento, e o código da unidade não vai daqui porque os códigos repetem
 * entre prédios — quem traduz id em código é o servidor, pela mesma fonte que
 * ofereceu a unidade.
 *
 * Quatro efeitos em três sistemas do outro lado, e o deal do Pipedrive é
 * escrito ANTES de o Sapron persistir (ADR 003). Uma recusa aqui significa que
 * nada mudou — é o que permite ao toast dizer a verdade.
 *
 * `journey_step` volta `null` quando a troca acontece: nenhuma versão avaliada
 * vale para a unidade nova. `trocada: false` é o caminho idempotente — pedir a
 * unidade que já está na indicação devolve 200 sem efeito nenhum.
 */
export const changeIndicationUnit = async (
  indicationId: string,
  unit: number,
): Promise<{
  journey_step: string | null;
  enterprise: number | null;
  enterprise_unit: number | null;
  trocada: boolean;
}> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/change-unit/`,
    { unit },
  );
  return data;
};

/**
 * Troca o EMPREENDIMENTO da indicação — criando outra indicação.
 *
 * Rota própria, e não o `create` público: aquele recusa indicação nova para um
 * investidor que já tem outra, então a troca pelo caminho público devolveria
 * 409 sempre — o mesmo beco que ela existe para fechar.
 *
 * Manda os dois ids: uma indicação sem unidade não existe (o fluxo padrão sai
 * da condição de venda dela), e o código da unidade repete entre prédios.
 */
export const changeIndicationEnterprise = async (
  indicationId: string,
  enterprise: number,
  unit: number,
): Promise<{
  indicacao_nova: number | null;
  enterprise: number | null;
  enterprise_unit: number | null;
  journey_step: string | null;
  trocada: boolean;
}> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/change-enterprise/`,
    { enterprise, enterprise_unit: unit },
  );
  return data;
};

/**
 * Pede aprovação manual para um fluxo reprovado.
 *
 * Abre card na fila do financeiro com a justificativa do corretor. Nada é
 * reservado aqui — quem efetiva é o kanban, se alguém aprovar.
 */
export const requestFlowApproval = async (
  indicationId: string,
  motivo: string,
): Promise<{
  journey_step: string;
  card_id: string | null;
  reserva_sera_efetivada_na_aprovacao: boolean | null;
}> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/request-approval/`,
    { motivo },
  );
  return data;
};

/**
 * Propõe um fluxo que passaria na régua, a partir do que o corretor montou.
 *
 * Não persiste nem avalia: devolve a sugestão para a tela oferecer "Aplicar
 * sugerido". Quem avalia continua sendo o `payment-flow/`, depois de aplicar —
 * assim o veredito sempre corresponde ao fluxo que está na tela.
 */
export const suggestPaymentFlow = async (
  indicationId: string,
  paymentFlow: unknown[],
  diaPagamento?: number,
): Promise<{
  valido: boolean;
  mensagem: string | null;
  ajustes: string[];
  payment_flow: { kind: string; amount: number; due_date: string }[];
}> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/suggest-payment-flow/`,
    diaPagamento
      ? { payment_flow: paymentFlow, dia_pagamento: diaPagamento }
      : { payment_flow: paymentFlow },
  );
  return data;
};

/**
 * Envia a indicação: executa a ação escolhida e cria o vínculo com o desfecho.
 *
 * O servidor confere que `acao` casa com o veredito **gravado** — pedir
 * `reservar` num fluxo reprovado devolve 409, não reserva por insistência do
 * cliente. Por isso a tela não precisa (e não deve) decidir isso sozinha.
 */
export const finalizeIndication = async (
  indicationId: string,
  acao: string,
  motivo?: string,
): Promise<{
  journey_step: string;
  acao: string;
  card_id: string | null;
  reservado: boolean | null;
}> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/finalize/`,
    motivo ? { acao, motivo } : { acao },
  );
  return data;
};

export const generateContract = async (indicationId: string): Promise<IInvestmentIndication> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/generate-contract/`,
  );
  return data;
};

/** Resposta de "gerar nova cobrança" — recusa vem aqui, não como erro. */
export interface NovaCobranca {
  gerada: boolean;
  /**
   * Por que não gerou. `COBRANCA_VIVA` e `CREDENCIAIS_BTG_INCOMPLETAS` pedem
   * coisas diferentes de quem lê, e é por isso que o servidor responde 200 com
   * o motivo em vez de um status de erro: a resposta atravessa três saltos até
   * aqui, e a cada salto um status HTTP vira `catch` de alguém.
   */
  motivo: string | null;
  detalhe: string | null;
  /** Se o botão continua valendo depois da tentativa. */
  pode_gerar_nova_cobranca: boolean;
  cobranca_status: string | null;
}

/**
 * Refaz o Pix quando a cobrança morreu.
 *
 * Sem corpo: qual contrato, qual valor e para quem sai tudo do estado gravado.
 * Mandá-los daqui deixaria o parceiro cobrar um valor que ele mesmo escreveu.
 */
export const generateNewCharge = async (indicationId: string): Promise<NovaCobranca> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/new-charge/`,
  );
  return data;
};

/**
 * Os dados que o contrato exige do comprador, mais o diagnóstico do que falta.
 *
 * Custa dois saltos acima da jornada — a fronteira resolve o token de completude
 * pelo deal e chama o completador —, e é por isso que este recurso NÃO se funde
 * ao `journey/`, que abre a cada card.
 *
 * O 404 da fronteira não sobe como erro: vira `existe: false` com
 * `motivo_bloqueio: 'contract_not_found'`. Contrato que ainda não nasceu não é
 * sistema fora do ar, e a tela precisa distinguir os dois.
 */
export const getContractData = async (indicationId: string): Promise<ContractData> => {
  const { data } = await request.get(
    `/partners/v2/indications/investment/${indicationId}/contract-data/`,
  );
  return data;
};

/**
 * Grava o preenchimento e devolve o diagnóstico atualizado.
 *
 * PATCH parcial nos dois campos de topo (`socios`, `documentos`): omitir um é
 * "não mexa nele". Dentro de `documentos`, porém, NÃO há merge — o mapa é
 * substituído por inteiro do outro lado da fronteira, então quem manda
 * documentos manda os cinco.
 *
 * Recusa com 409 `contract_multiple_partners` quando o contrato tem mais de um
 * sócio. A contagem é feita sobre o contrato real, não sobre este payload:
 * mandar um sócio para um contrato de dois é justamente o caso que apagaria o
 * segundo sem ninguém perceber.
 */
export const patchContractData = async (
  indicationId: string,
  payload: ContractDataPatch,
): Promise<ContractDataPatchResult> => {
  const { data } = await request.patch(
    `/partners/v2/indications/investment/${indicationId}/contract-data/`,
    payload,
  );
  return data;
};

/**
 * Envia o contrato para assinatura: PDF, envelope e e-mail ao investidor.
 *
 * O passo mais caro de errar da jornada. Do outro lado da fronteira a chamada
 * gera o PDF, cria o envelope no sistema de assinatura e dispara o e-mail — e
 * nada disso é desfeito por uma segunda chamada.
 *
 * Sem corpo de propósito: `reenviar` existe na fronteira e fica FECHADO nesta
 * rota (o servidor manda sempre `false`). Substituir um envelope vivo é o
 * caminho da assinatura expirada, decidido em outro lugar.
 *
 * Duas recusas que não são "erro de sistema" e por isso não podem virar toast
 * genérico: 501 `contract_emission_not_available` (a flag de emissão está
 * desligada — a emissão segue sendo do time interno) e 409
 * `contract_data_incomplete` (falta preencher). Quem as traduz é `emissao.ts`.
 */
export const emitContract = async (indicationId: string): Promise<EmissaoContratoResultado> => {
  const { data } = await request.post(
    `/partners/v2/indications/investment/${indicationId}/emit-contract/`,
  );
  return data;
};

export const getInvestmentIndicationSummary = async (): Promise<IInvestmentIndicationSummary> => {
  const { data } = await request.get('/partners/indications-investment/summary');
  return data;
};

export const getPropertyIndicationSummary = async (): Promise<IPropertyIndicationSummary> => {
  const { data } = await request.get('/partners/indications-property/summary');
  return data;
};

export const getBuildingIndicationSummary = async (): Promise<IBuildingIndicationSummary> => {
  const { data } = await request.get('/partners/indications-building/summary');
  return data;
};

export const getAllotmentIndicationSummary = async (): Promise<IAllotmentIndicationSummary> => {
  const { data } = await request.get('/partners/indications-allotment/summary');
  return data;
};

export const getNewAllotmentIndicationSummary = async (): Promise<IAllotmentIndicationSummary> => {
  const { data } = await request.get('/partners/indication-allotments/summary');
  return data;
};

export const getPartnerIndicationSummary = async (): Promise<IPartnerIndicationSummary> => {
  const { data } = await request.get('/partners/indications/partner/summary/');
  return data;
};

export const getFranchiseIndicationSummary = async (): Promise<IIndicationSummary> => {
  const { data } = await request.get('/api/v1/partners/indications-franchise/summary/');
  return data;
};

export const getDecorIndicationSummary = async (): Promise<IIndicationSummary> => {
  const { data } = await request.get('/partners/indications-decor/summary/');
  return data;
};

export const getMarketplaceIndicationSummary = async (): Promise<IIndicationSummary> => {
  const { data } = await request.get('/partners/indications-marketplace/summary/');
  return data;
};

export const getB2BCondominium = async (): Promise<RequestB2BCondominium[]> => {
  const { data } = await request.get('/partners/b2b_condominium/');
  return data;
};

export const patchUser = async (id: number, data: UserPatch) => {
  await request.patch(`/account/user/${id}/`, {
    ...data,
  });
};

export const postBuilderIndication = async (data: IBuilderIndication): Promise<any> => {
  const partnerId = (data as any).partner ?? (data as any).partner_id ?? undefined;
  const payload = {
    ...data,
    partner_id: partnerId,
  };
  const { data: response } = await request.post('/partners/v2/indications/building/', payload);
  return response;
};

export const getBuilderIndication = async (
  payload: IndicationParams,
): Promise<IndicationResponse<IBuilderIndication>> => {
  const { data } = await request.get(`/partners/v2/indications/building/`, {
    params: payload,
  });
  return {
    results: data.results || data,
    total_pages: data.total_pages || 1,
    count: data.count || 0,
  };
};

export const getBuilderStates = async (): Promise<IBuilderState[]> => {
  const { data } = await request.get('/partners/pipedrive/states/');
  return data;
};

export const getBuilderCities = async (stateCode?: string): Promise<IBuilderCity[]> => {
  const params = stateCode ? { state_code: stateCode } : {};
  const { data } = await request.get('/partners/pipedrive/cities/', { params });
  return data;
};

export const getOrganizationPartnersMe = async (): Promise<any[]> => {
  const { data } = await request.get('/organization/partners/me');
  return data;
};

export const getAllotmentCities = async (state?: string): Promise<IAllotmentCity[]> => {
  const params = state ? { state } : {};
  const { data } = await request.get('/partners/pipefy/allotment-cities', { params });
  return data;
};

export const getAllotmentNeighborhoods = async (
  city_id?: number,
): Promise<IAllotmentNeighborhood[]> => {
  const params = city_id ? { city_id } : {};
  const { data } = await request.get('/partners/pipefy/allotment-neighborhoods', { params });
  return data;
};

export const postAllotmentIndicationNew = async (
  data: IAllotmentIndicationPostNew,
): Promise<IAllotmentIndicationNew> => {
  const { data: response } = await request.post('/partners/v2/indication/allotments/', data);
  return response;
};

export const getIndicationCoordinates = async (params?: {
  partner_id?: string;
  status?: string;
}): Promise<IIndicationCoordinate[]> => {
  const { data } = await request.get('/api/v1/partners/indication-allotments/coordinates', {
    params,
  });
  return data;
};

export const getIndicationDetail = async (
  id: number | string,
  params?: { partner_id?: string | number },
): Promise<IIndicationDetail> => {
  const { data } = await request.get(`/api/v1/partners/indication-allotments/${id}`, {
    params,
  });
  return data;
};

export const postPartnerIndication = async (data: IPartnerIndicationPost): Promise<any> => {
  const { data: response } = await request.post('/partners/indications/partner/', data);
  return response;
};

/** Grupos de parceiros por região, com o link de convite vigente. */
export const getPartnerWhatsAppGroups = async (): Promise<IPartnerWhatsAppGroup[]> => {
  const { data } = await request.get('/partners/whatsapp-groups/');
  return Array.isArray(data) ? data : (data?.results ?? []);
};

/** O link vigente do convite, o mesmo que já foi mandado. 404 quando ainda não há. */
export const getPartnerInvite = async (id: number | string): Promise<IPartnerInvite> => {
  const { data } = await request.get(`/partners/indications/partner/${id}/invite/`);
  return data;
};

/** Gera (ou renova) o link do convite. O anterior deixa de valer. */
export const postPartnerInvite = async (id: number | string): Promise<IPartnerInvite> => {
  const { data } = await request.post(`/partners/indications/partner/${id}/invite/`);
  return data;
};

/** Marca que o parceiro mandou o link pelo WhatsApp dele ou copiou. */
export const postPartnerInviteSent = async (
  id: number | string,
  channel: PartnerInviteChannel,
): Promise<unknown> => {
  const { data } = await request.post(`/partners/indications/partner/${id}/invite/sent/`, {
    channel,
  });
  return data;
};

export const getPartnerIndication = async (
  payload: IndicationParams,
): Promise<IndicationResponse<any>> => {
  const { data } = await request.get(`/partners/indications/partner/`, {
    params: payload,
  });
  return {
    results: data.results || data,
    total_pages: data.total_pages || 1,
    count: data.count || 0,
  };
};

export const getFranchiseIndication = async (
  payload: IndicationParams,
): Promise<IndicationResponse<any>> => {
  const { data } = await request.get(`/partners/v2/indications/franchise/`, {
    params: payload,
  });
  return {
    results: data.results,
    total_pages: data.total_pages,
    count: data.count,
  };
};

export const getDecorIndication = async (
  payload: IndicationParams,
): Promise<IndicationResponse<any>> => {
  const { data } = await request.get(`/partners/v2/indications/decor/`, {
    params: payload,
  });
  return {
    results: data.results,
    total_pages: data.total_pages,
    count: data.count,
  };
};

export const getPartnerBanners = async (location: string): Promise<IBanner[]> => {
  const { data } = await request.get('/api/v1/banners/me', {
    params: {
      location,
    },
  });
  return data;
};

export const getPartnerRanking = async (params: RankingParams): Promise<RankingResponse> => {
  const { data } = await request.get('/api/v1/partners/indication-ranking', {
    params,
  });
  return data;
};

export const postFranchiseIndication = async (data: IFranchiseIndicationPost): Promise<any> => {
  const { data: response } = await request.post('/partners/v2/indications/franchise/', data);
  return response;
};

export const getColdInvestmentIndications = async (
  payload: Pick<IndicationParams, 'page' | 'page_size' | 'partner_id'>,
): Promise<IndicationResponse<IColdInvestmentIndicationListItem>> => {
  const { data } = await request.get('/partners/v2/indications/cold-investment/', {
    params: payload,
  });

  return { results: data.results, total_pages: data.total_pages, count: data.count };
};

export const postColdInvestmentIndication = async (
  data: IColdInvestmentIndication,
): Promise<any> => {
  const { data: response } = await request.post('/partners/v2/indications/cold-investment/', data);
  return response;
};

export const postDecorIndication = async (data: IDecorIndicationPost): Promise<any> => {
  const { data: response } = await request.post('/partners/v2/indications/decor/', data);
  return response;
};

export const postMarketplaceIndication = async (data: IMarketplaceIndicationPost): Promise<any> => {
  const partnerId = (data as any).partner ?? (data as any).partner_id ?? undefined;
  const payload = {
    ...data,
    partner_id: partnerId,
  };
  const { data: response } = await request.post('/partners/indications/marketplace/', payload);
  return response;
};

export const getMarketplaceIndication = async (
  payload: IndicationParams,
): Promise<IndicationResponse<IMarketplaceIndication>> => {
  const { data } = await request.get(`/partners/indications/marketplace/`, {
    params: payload,
  });

  return {
    results: data.results || data,
    total_pages: data.total_pages || 1,
    count: data.count || 0,
  };
};

// Commission management functions
export const getPendingFixedCommissions = async (queryParams: string = '') => {
  const { data } = await request.get(
    `/partners/indications/property/pending-fixed-commissions/${queryParams}`,
  );
  return data;
};

export const createFixedCommission = async (payload: {
  indication_id: number;
  value?: number;
  date_ref?: string;
  notes?: string;
  type?: string;
}) => {
  const { data } = await request.post(
    '/partners/indications/property/create-fixed-commission/',
    payload,
  );
  return data;
};

export const registerCommissionOrRepassed = async (payload: {
  indication_ids: number[];
  action: 'register' | 'repassed';
  value?: number;
  date_ref?: string;
  notes?: string;
  type?: string;
  custom_type?: string;
}) => {
  const { data } = await request.post(
    '/partners/indications/property/register-commission-or-repassed/',
    payload,
  );
  return data;
};

export const createManualCommission = async (payload: {
  partner_id: number;
  value: number;
  date_ref?: string;
  type: string;
  custom_type?: string;
  notes?: string;
  property_id?: number;
  payment_method?: CommissionPaymentMethod;
}) => {
  const { data } = await request.post(
    '/partners/indications/property/create-manual-commission/',
    payload,
  );
  return data;
};

const LOCATION_BASE = '/partners/indications/location';

export const getLocationIndications = async (
  params: LocationIndicationParams,
): Promise<LocationIndicationListResponse> => {
  const { data } = await request.get(`${LOCATION_BASE}/`, { params });
  return {
    results: data.results || [],
    count: data.count || 0,
    total_pages: data.total_pages || 1,
  };
};

/** Fluxo antigo (formulário de 2 etapas): cria e gera o contrato numa tacada. */
export const postLocationGenerateContract = async (
  payload: LocationGenerateContractPayload,
): Promise<LocationGenerateContractResponse> => {
  const { data } = await request.post(`${LOCATION_BASE}/generate-contract/`, payload);
  return data;
};

export const getLocationIndicationSummary = async (): Promise<LocationIndicationSummary> => {
  const { data } = await request.get(`${LOCATION_BASE}/summary/`);
  return {
    active: Number(data?.active ?? 0),
    closing: Number(data?.closing ?? 0),
    won_year: Number(data?.won_year ?? 0),
  };
};

export const getLocationIndicationDetail = async (
  id: number | string,
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.get(`${LOCATION_BASE}/${id}/`);
  return data;
};

/** Indicação mínima: só o contato. Já cria o deal no CRM. */
export const createLocationIndication = async (
  payload: LocationIndicationCreatePayload,
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.post(`${LOCATION_BASE}/`, payload);
  return data;
};

/** Rascunho: imóvel, perguntas, proposta e dados do contrato. 409 depois do contrato gerado. */
export const patchLocationIndication = async (
  id: number | string,
  payload: LocationIndicationDraftPayload,
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.patch(`${LOCATION_BASE}/${id}/`, payload);
  return data;
};

/**
 * Troca a comissão (R$ 500 ou 2%) até o ganho. Vai sozinha no corpo: com o deal criado, o back
 * grava no card do Pipedrive e responde 409 depois do ganho ou com acordo comercial no card.
 */
export const patchLocationIndicationCommission = async (
  id: number | string,
  commissionChoice: 'fixed_500' | 'recurring_2',
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.patch(`${LOCATION_BASE}/${id}/`, {
    commission_choice: commissionChoice,
  });
  return data;
};

/** Confere um endereço solto no polígono. Não grava nada. */
export const checkLocationCoverage = async (
  payload: LocationCoverageCheckPayload,
): Promise<LocationCoverageCheckResponse> => {
  const { data } = await request.post(`${LOCATION_BASE}/coverage-check/`, payload);
  return data;
};

/** Confere o endereço salvo na indicação e grava `coverage_status`. */
export const checkLocationIndicationCoverage = async (
  id: number | string,
): Promise<LocationCoverageCheckResponse> => {
  const { data } = await request.post(`${LOCATION_BASE}/${id}/coverage-check/`);
  return data;
};

/**
 * Confere as respostas do imóvel no SLA (a mesma régua da LP). Não grava nada: o veredito que
 * vale no contrato é gravado no PATCH do rascunho, pelo próprio back.
 */
export const checkLocationQualification = async (
  payload: LocationQualificationCheckPayload,
): Promise<LocationQualificationCheckResponse> => {
  const { data } = await request.post(`${LOCATION_BASE}/qualification-check/`, payload);
  return data;
};

/** Gera o contrato a partir de uma indicação existente (proposta + cobertura + CPF). */
export const generateLocationContractFor = async (
  id: number | string,
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.post(`${LOCATION_BASE}/${id}/generate-contract/`);
  return data;
};

/**
 * Anexa o comprovante de endereço do imóvel depois do contrato gerado: o back repassa ao serviço
 * de contratos e, se era só isso que faltava, o envelope sai.
 */
export const sendLocationAddressProof = async (
  id: number | string,
  fileUid: string,
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.post(`${LOCATION_BASE}/${id}/address-proof/`, {
    file_uid: fileUid,
  });
  return data;
};

export const resendLocationContractEmail = async (
  id: number | string,
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.post(`${LOCATION_BASE}/${id}/resend-contract-email/`);
  return data;
};

export const renewLocationContractLink = async (
  id: number | string,
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.post(`${LOCATION_BASE}/${id}/renew-link/`);
  return data;
};

export const cancelLocationIndication = async (
  id: number | string,
  reason: string,
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.post(`${LOCATION_BASE}/${id}/cancel/`, { reason });
  return data;
};

export const transferLocationIndicationToSeazone = async (
  id: number | string,
  note: string,
  kind?: 'proposal',
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.post(`${LOCATION_BASE}/${id}/transfer-to-seazone/`, {
    note,
    ...(kind && { kind }),
  });
  return data;
};

/** Exceção do time interno (Admin/SeazoneAdministrative); o back recusa o parceiro com 403. */
export const confirmLocationIndicationPayment = async (
  id: number,
): Promise<ILocationIndicationDetail> => {
  const { data } = await request.post(`${LOCATION_BASE}/${id}/confirm-payment/`);
  return data;
};
